import { useCallback, useEffect, useRef, useState } from 'react';
import { Eye } from 'lucide-react';

/**
 * Slider-based before (original) / after (makeup) compare.
 */
export default function LooksCompareSlider({
  beforeSrc,
  afterSrc,
  position,
  onPositionChange,
  className = '',
}) {
  const stageRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pos = Math.min(100, Math.max(0, Number(position) || 50));

  const setFromClientX = useCallback(
    (clientX) => {
      const el = stageRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const next = ((clientX - rect.left) / rect.width) * 100;
      onPositionChange(Math.min(100, Math.max(0, next)));
    },
    [onPositionChange],
  );

  const onPointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    setFromClientX(event.clientX);
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (event) => setFromClientX(event.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, setFromClientX]);

  return (
    <div
      ref={stageRef}
      className={`looks-compare-stage${className ? ` ${className}` : ''}${dragging ? ' is-dragging' : ''}`}
    >
      <img src={afterSrc} alt="After makeup" className="looks-compare-stage__img looks-compare-stage__after" />
      <div
        className="looks-compare-stage__before-clip"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img src={beforeSrc} alt="Before makeup" className="looks-compare-stage__img" />
      </div>

      <span className="looks-compare-stage__label looks-compare-stage__label--before">Before</span>
      <span className="looks-compare-stage__label looks-compare-stage__label--after">After</span>

      <div
        className="looks-compare-stage__divider"
        style={{ left: `${pos}%` }}
        onPointerDown={onPointerDown}
      >
        <button
          type="button"
          className="looks-compare-stage__handle"
          aria-label="Drag compare slider"
          onPointerDown={onPointerDown}
        >
          <Eye size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
